import {xorString} from '../util/xorString';
import ServerApi from '../server-api/serverApi';
import {generateCodeVerifier, generateCodeChallenge} from '../util/pkce';

export enum TokenType {
  guest,
  subscriber
}

export type TokenData = {
  token: string,
  type: TokenType,
  granted: number
  expiresIn: number
  refreshToken?: string
}

const guestTokenKey = '1sP#tF~nC_Hr!td_';

type GuestTokenResponse = {
  t: string,
  tt: string,
  e: string
}

// Regex to match authorization code in query parameters
const spotifyCallbackCodeRx = /[?&]code=([^&]+)/;

class Authenticator {
  private readonly clientId = 'b0583876218d439fb2229e67cee47962';
  private readonly authorizeUrlBase = 'https://accounts.spotify.com/authorize';
  private readonly baseUrl = document.location.protocol + '//' + document.location.host;
  private readonly redirectUri = (this.baseUrl.includes('localhost') ? 'http://127.0.0.1:3000?run' : this.baseUrl + '?run');

  // todo: change to uncharted.com
  private readonly spotifyUnchartedServerBase = ServerApi.serverBase;
  private readonly guestTokenUrl = `${this.spotifyUnchartedServerBase}/auth/unscoped`;

  private readonly scopes = [
    'user-follow-read',
    'user-follow-modify',

    'user-library-read',
    'user-library-modify ',

    'user-read-currently-playing',
    'user-read-playback-state',
    'user-modify-playback-state',

    'user-top-read',
    'user-read-recently-played',
    'user-read-private',

    'playlist-read-private',
    'playlist-modify-public',
    'playlist-modify-private'
  ];

  private readonly tokenKey = 'spotify-uncharted-token';
  private readonly redirectHash = 'spotify-uncharted-redirect-hash';
  private readonly codeVerifierKey = 'spotify-uncharted-code-verifier';
  private readonly tokenUrl = 'https://accounts.spotify.com/api/token';

  private token: TokenData | null = null;

  hasAuth(): boolean {
    const token = window.localStorage.getItem(this.tokenKey);
    if (!token) {
      return false;
    }
    try {
      JSON.parse(token || '');
      return true;
    } catch {
      return false;
    }
  }

  isAuthBacklink(): boolean {
    return !!window.location.search.match(spotifyCallbackCodeRx);
  }

  async getAuthRedirectUrl(): Promise<string> {
    window.localStorage.setItem(this.redirectHash, document.location.search + document.location.hash);
    
    // Generate PKCE parameters
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    
    // Store code_verifier in sessionStorage for later use in token exchange
    window.sessionStorage.setItem(this.codeVerifierKey, codeVerifier);
    
    // Build authorization URL with PKCE parameters
    const params = new URLSearchParams({
      client_id: this.clientId,
      response_type: 'code',
      redirect_uri: this.redirectUri,
      scope: this.scopes.join(','),
      code_challenge_method: 'S256',
      code_challenge: codeChallenge
    });
    
    return `${this.authorizeUrlBase}?${params.toString()}`;
  }

  async refreshAccessToken(): Promise<TokenData | null> {
    // Get current token from storage
    const storedToken = window.localStorage.getItem(this.tokenKey);
    if (!storedToken) {
      return null;
    }

    let tokenData: TokenData;
    try {
      tokenData = JSON.parse(storedToken) as TokenData;
    } catch {
      return null;
    }

    // Check if refresh token exists
    if (!tokenData.refreshToken || tokenData.type !== TokenType.subscriber) {
      return null;
    }

    // Prepare refresh token request
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: tokenData.refreshToken,
      client_id: this.clientId
    });

    // Refresh the access token
    const response = await fetch(this.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString()
    });

    if (!response.ok) {
      // If refresh fails, clear the token
      window.localStorage.removeItem(this.tokenKey);
      this.token = null;
      return null;
    }

    const data = await response.json();

    // Update token data with new access token
    // Spotify may return a new refresh token, or we keep the existing one
    const updatedTokenData: TokenData = {
      token: data.access_token,
      type: TokenType.subscriber,
      granted: Date.now(),
      expiresIn: data.expires_in,
      refreshToken: data.refresh_token || tokenData.refreshToken
    };

    // Store updated token in localStorage
    window.localStorage.setItem(this.tokenKey, JSON.stringify(updatedTokenData));
    this.token = updatedTokenData;

    return updatedTokenData;
  }

  private async exchangeCodeForToken(code: string): Promise<TokenData> {
    // Retrieve and remove code_verifier from sessionStorage
    const codeVerifier = window.sessionStorage.getItem(this.codeVerifierKey);
    if (!codeVerifier) {
      throw new Error('Code verifier not found in session storage');
    }
    window.sessionStorage.removeItem(this.codeVerifierKey);

    // Prepare token exchange request
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: this.redirectUri,
      client_id: this.clientId,
      code_verifier: codeVerifier
    });

    // Exchange authorization code for tokens
    const response = await fetch(this.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Token exchange failed: ${error.error || response.statusText}`);
    }

    const data = await response.json();
    
    // Create token data with refresh token
    const tokenData: TokenData = {
      token: data.access_token,
      type: TokenType.subscriber,
      granted: Date.now(),
      expiresIn: data.expires_in,
      refreshToken: data.refresh_token
    };

    // Store token in localStorage
    window.localStorage.setItem(this.tokenKey, JSON.stringify(tokenData));
    this.token = tokenData;

    return tokenData;
  }

  async authorize(): Promise<TokenData> {
    // Try to process Spotify auth callback (authorization code in query params)
    const codeMatch = window.location.search.match(spotifyCallbackCodeRx);
    if (codeMatch && codeMatch[1]) {
      try {
        const tokenData = await this.exchangeCodeForToken(codeMatch[1]);
        // Restore the original URL hash/query after successful token exchange
        const hash = window.localStorage.getItem(this.redirectHash);
        window.localStorage.removeItem(this.redirectHash);
        document.location.replace(this.baseUrl + (hash || ''));
        return tokenData;
      } catch (error) {
        // If token exchange fails, redirect to base URL
        console.error('Token exchange failed:', error);
        document.location.replace(this.baseUrl);
        throw error;
      }
    }

    // Try to get already existing token ('logged in' case)
    const _token = window.localStorage.getItem(this.tokenKey);
    try {
      this.token = _token ? (JSON.parse(_token) as TokenData) : null;
    } catch {
      this.token = null;
    }

    if (this.token) {
      // Check if token is valid (not expired)
      const isValidToken = (Date.now() - this.token.granted) / 1000 < this.token.expiresIn;
      if (!isValidToken) {
        if (this.token.type === TokenType.subscriber) {
          // Attempt to refresh the token before redirecting to login
          const refreshedToken = await this.refreshAccessToken();
          if (refreshedToken) {
            this.token = refreshedToken;
            return this.token;
          }
          // If refresh fails, redirect to login page
          window.localStorage.removeItem(this.tokenKey);
          document.location.replace(this.baseUrl);
        } else {
          // For guest tokens, just remove and get a new one
          window.localStorage.removeItem(this.tokenKey);
          try {
            const response = await fetch(this.guestTokenUrl);
            if (!response.ok) {
              // Redirect to login page (base URL) without any hash
              document.location.replace(this.baseUrl);
              throw new Error('Failed to get guest token');
            }
            const data = (await response.json()) as GuestTokenResponse;
            this.token = {
              token: xorString(data.t, guestTokenKey),
              type: TokenType.guest,
              granted: Date.now(),
              expiresIn: parseInt(data.e)
            };
            window.localStorage.setItem(this.tokenKey, JSON.stringify(this.token));
          } catch (error) {
            // Network error or fetch failure - redirect to login page (base URL) without any hash
            document.location.replace(this.baseUrl);
            throw error;
          }
        }
      }
    } else {
      // No token exists, get guest token
      try {
        const response = await fetch(this.guestTokenUrl);
        if (!response.ok) {
          // Redirect to login page (base URL) without any hash if you can't get the guest token
          document.location.replace(this.baseUrl);
          throw new Error('Failed to get guest token');
        }
        const data = (await response.json()) as GuestTokenResponse;
        this.token = {
          token: xorString(data.t, guestTokenKey),
          type: TokenType.guest,
          granted: Date.now(),
          expiresIn: parseInt(data.e)
        };
        window.localStorage.setItem(this.tokenKey, JSON.stringify(this.token));
      } catch (error) {
        // Network error or fetch failure - redirect to login page (base URL) without any hash
        document.location.replace(this.baseUrl);
        throw error;
      }
    }
    return this.token;
  }

  logout() : void {
    window.localStorage.removeItem(this.tokenKey);
    document.location.replace(this.baseUrl);
  }
}

export default Authenticator;